import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAdminAuth } from "@/components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AdminSaaSOverview() {
  const { admin } = useAdminAuth();
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, healthRes] = await Promise.all([
          axios.get(`${API}/admin/saas/overview`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/saas/health`, { headers: getAuthHeaders() }),
        ]);
        setOverview(overviewRes.data);
        setHealth(healthRes.data);
      } catch (error) {
        console.error("Failed to fetch SaaS data:", error);
        toast.error("Failed to load SaaS metrics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const MetricCard = ({ title, value, subtitle, trend }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-3xl font-bold">{value}</CardTitle>
      </CardHeader>
      {subtitle && (
        <CardContent>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </CardContent>
      )}
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="admin-saas-overview">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">SaaS Intelligence Overview</h1>
          <p className="text-muted-foreground">Business metrics at a glance</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm ${health?.status === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {health?.status === 'healthy' ? 'All Systems Healthy' : 'Issues Detected'}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Activation Rate"
          value={`${overview?.activation_rate || 0}%`}
          subtitle="Users completing first analysis"
        />
        <MetricCard
          title="Revenue / Active User"
          value={`$${overview?.revenue_per_active_user?.toFixed(2) || '0.00'}`}
          subtitle="Last 30 days"
        />
        <MetricCard
          title="Avg Credit Margin"
          value={`$${overview?.average_credit_margin?.toFixed(4) || '0.0000'}`}
          subtitle="Per credit used"
        />
        <MetricCard
          title="Expansion Opportunities"
          value={overview?.expansion_opportunity_count || 0}
          subtitle="Users ready for upgrade"
        />
      </div>

      {/* Churn Risk Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Churn Risk Distribution</CardTitle>
          <CardDescription>Paying users by risk band</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-700">
                {overview?.churn_risk_distribution?.low || 0}
              </p>
              <p className="text-sm text-green-600">Low Risk</p>
            </div>
            <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">
                {overview?.churn_risk_distribution?.medium || 0}
              </p>
              <p className="text-sm text-yellow-600">Medium Risk</p>
            </div>
            <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-red-700">
                {overview?.churn_risk_distribution?.high || 0}
              </p>
              <p className="text-sm text-red-600">High Risk</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Users</CardDescription>
            <CardTitle className="text-2xl">{overview?.total_users || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Paying Users</CardDescription>
            <CardTitle className="text-2xl">{overview?.paying_users || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Users (30d)</CardDescription>
            <CardTitle className="text-2xl">{overview?.active_users_30d || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Collection Health */}
      {health?.collections && (
        <Card>
          <CardHeader>
            <CardTitle>Collection Health</CardTitle>
            <CardDescription>SaaS Intelligence data stores</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              {Object.entries(health.collections).map(([name, count]) => (
                <div key={name} className="bg-muted rounded-lg p-3">
                  <p className="text-xl font-semibold">{count}</p>
                  <p className="text-xs text-muted-foreground">{name.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
